import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  Archive,
  CheckCircle2,
  ClipboardList,
  Eye,
  FileInput,
  Info,
  RefreshCw,
  X,
} from "lucide-react";
import { toast } from "react-toastify";

import { useAuth } from "../../app/providers/AuthProvider";
import { FK } from "../../capabilities/featureKeys";
import {
  archiveImportedFlow,
  getImportedFlow,
  getProviderFlow,
  getWhatsAppFlowErrorMessage,
  importProviderFlow,
  listImportedFlows,
  listProviderFlows,
  registerWhatsAppFlowDataExchange,
  resyncImportedFlow,
} from "../../api/whatsAppFlows";

const permission = (can, hasAllAccess, code) =>
  Boolean(hasAllAccess || (typeof can === "function" && can(code)));

const text = value => (value == null || value === "" ? "—" : String(value));

const isDataExchangeMode = value =>
  value === 1 || String(value).toLowerCase() === "dataexchange";

const modeLabel = value => {
  if (value === 0 || String(value).toLowerCase() === "endpointless") {
    return "Endpointless";
  }
  if (isDataExchangeMode(value)) {
    return "Data Exchange";
  }
  return text(value);
};

const formatDate = value => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : new Intl.DateTimeFormat(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(date);
};

export const isEndpointlessImportEligible = detail =>
  Boolean(
    detail &&
      detail.status === "PUBLISHED" &&
      detail.isEndpointless === true &&
      (detail.isImportEligible ??
        (Array.isArray(detail.validationErrors) &&
          detail.validationErrors.length === 0)),
  );

export const isDataExchangeRegistrationEligible = detail =>
  Boolean(
    detail &&
      detail.status === "PUBLISHED" &&
      detail.isEndpointless === false &&
      Array.isArray(detail.validationErrors) &&
      detail.validationErrors.length === 0,
  );

export const isProviderFlowEligible = isEndpointlessImportEligible;

function Badge({ children, tone = "slate" }) {
  const tones = {
    green: "border-emerald-200 bg-emerald-50 text-emerald-700",
    amber: "border-amber-200 bg-amber-50 text-amber-800",
    red: "border-red-200 bg-red-50 text-red-700",
    blue: "border-blue-200 bg-blue-50 text-blue-700",
    slate: "border-slate-200 bg-slate-50 text-slate-700",
  };
  return (
    <span
      className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${tones[tone] || tones.slate}`}
    >
      {children}
    </span>
  );
}

function StatusBadge({ status }) {
  const normalized = String(status || "Unknown").toUpperCase();
  const tone =
    normalized === "PUBLISHED" || normalized === "IMPORTED"
      ? "green"
      : normalized === "DRAFT"
        ? "blue"
        : normalized === "DEPRECATED" ||
            normalized === "BLOCKED" ||
            normalized.includes("ERROR") ||
            normalized === "UNSUPPORTED"
          ? "red"
          : "slate";
  return <Badge tone={tone}>{text(status || "Unknown")}</Badge>;
}

function WarningList({ warnings }) {
  if (!Array.isArray(warnings) || warnings.length === 0) return null;
  return (
    <ul className="space-y-1 text-sm text-amber-800">
      {warnings.map((warning, index) => (
        <li key={`${warning?.code || "warning"}-${index}`} className="flex gap-2">
          <AlertTriangle size={15} className="mt-0.5 shrink-0" />
          <span>{warning?.message || "Provider warning."}</span>
        </li>
      ))}
    </ul>
  );
}

function LoadingSkeleton({ rows = 4 }) {
  return (
    <div aria-label="Loading WhatsApp Forms" className="space-y-3">
      {Array.from({ length: rows }, (_, index) => (
        <div
          key={index}
          className="h-16 animate-pulse rounded-lg border border-slate-200 bg-slate-100"
        />
      ))}
    </div>
  );
}

function EmptyState({ title, message }) {
  return (
    <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-6 py-12 text-center">
      <FileInput className="mx-auto mb-3 text-slate-400" size={30} />
      <h3 className="font-semibold text-slate-800">{title}</h3>
      <p className="mx-auto mt-1 max-w-xl text-sm text-slate-600">{message}</p>
    </div>
  );
}

function ErrorState({ error, onRetry }) {
  return (
    <div
      role="alert"
      className="rounded-xl border border-red-200 bg-red-50 p-5 text-red-800"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 shrink-0" size={20} />
        <div className="flex-1">
          <p className="font-semibold">Unable to load WhatsApp Forms</p>
          <p className="mt-1 text-sm">{getWhatsAppFlowErrorMessage(error)}</p>
          <button
            type="button"
            onClick={onRetry}
            className="mt-3 rounded-md border border-red-300 bg-white px-3 py-1.5 text-sm font-semibold hover:bg-red-100"
          >
            Retry
          </button>
        </div>
      </div>
    </div>
  );
}

function Modal({ open, title, onClose, children, width = "max-w-3xl" }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = event => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose, open]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/50 p-4"
      role="presentation"
      onMouseDown={event => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`max-h-[90vh] w-full ${width} overflow-hidden rounded-xl bg-white shadow-2xl`}
      >
        <header className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
          <button
            type="button"
            aria-label="Close dialog"
            onClick={onClose}
            className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-800"
          >
            <X size={20} />
          </button>
        </header>
        <div className="max-h-[calc(90vh-65px)] overflow-y-auto p-5">{children}</div>
      </section>
    </div>
  );
}

function DetailGrid({ items }) {
  return (
    <dl className="grid grid-cols-1 gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4 sm:grid-cols-2">
      {items.map(item => (
        <div key={item.label}>
          <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {item.label}
          </dt>
          <dd className="mt-1 break-words text-sm text-slate-900">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function ActionConfirmation({
  confirmation,
  busy,
  onCancel,
  onConfirm,
}) {
  if (!confirmation) return null;
  const copy = {
    import: {
      title: "Import WhatsApp Form",
      message:
        "Import this published WhatsApp Form into XploreByte? Its current schema will be stored as an immutable revision.",
      action: "Import Form",
    },
    register: {
      title: "Register Data Exchange Flow",
      message:
        "Register this published Data Exchange Flow in XploreByte? Its supplied schema will be stored as an immutable revision.",
      action: "Register Flow",
    },
    resync: {
      title: "Resync WhatsApp Form",
      message:
        "Check Meta for changes to this published WhatsApp Form? XploreByte will not overwrite the current immutable revision.",
      action: "Resync",
    },
    archive: {
      title: "Archive WhatsApp Form",
      message:
        "Archive this WhatsApp Form in XploreByte? This will not delete or modify the Flow in Meta.",
      action: "Archive locally",
    },
  }[confirmation.type];

  return (
    <Modal open title={copy.title} onClose={busy ? () => {} : onCancel} width="max-w-lg">
      <p className="text-sm leading-6 text-slate-700">{copy.message}</p>
      <div className="mt-6 flex justify-end gap-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={busy}
          className="rounded-md bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {busy ? "Working…" : copy.action}
        </button>
      </div>
    </Modal>
  );
}

export default function WhatsAppFlowsPage() {
  const { can, hasAllAccess } = useAuth();
  const canView = permission(can, hasAllAccess, FK.WHATSAPP_FLOW_VIEW);
  const canImport =
    permission(can, hasAllAccess, FK.WHATSAPP_FLOW_CREATE) &&
    permission(can, hasAllAccess, FK.WHATSAPP_FLOW_PUBLISH);
  const canResync = permission(can, hasAllAccess, FK.WHATSAPP_FLOW_EDIT);
  const canArchive = permission(can, hasAllAccess, FK.WHATSAPP_FLOW_ARCHIVE);

  const [activeTab, setActiveTab] = useState("provider");
  const [provider, setProvider] = useState({
    loading: true,
    items: [],
    error: null,
  });
  const [imported, setImported] = useState({
    loading: true,
    items: [],
    error: null,
  });
  const [providerDetail, setProviderDetail] = useState(null);
  const [providerDetailLoading, setProviderDetailLoading] = useState(false);
  const [providerDetailError, setProviderDetailError] = useState(null);
  const [importedDetail, setImportedDetail] = useState(null);
  const [importedDetailLoading, setImportedDetailLoading] = useState(false);
  const [importedDetailError, setImportedDetailError] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  const [busyAction, setBusyAction] = useState("");
  const [actionError, setActionError] = useState(null);
  const [dataExchangeSchema, setDataExchangeSchema] = useState("");
  const [dataExchangeSchemaError, setDataExchangeSchemaError] = useState("");
  const actionLockRef = useRef(false);

  const loadProvider = useCallback(async () => {
    setProvider(current => ({ ...current, loading: true, error: null }));
    try {
      const result = await listProviderFlows();
      setProvider({
        loading: false,
        items: Array.isArray(result?.items) ? result.items : [],
        error: null,
      });
    } catch (error) {
      setProvider({ loading: false, items: [], error });
    }
  }, []);

  const loadImported = useCallback(async () => {
    setImported(current => ({ ...current, loading: true, error: null }));
    try {
      const rows = await listImportedFlows();
      const items = Array.isArray(rows) ? rows : [];
      const hydrated = await Promise.all(
        items.map(async item => {
          try {
            return { ...item, detail: await getImportedFlow(item.id) };
          } catch {
            return { ...item, detail: null };
          }
        }),
      );
      setImported({ loading: false, items: hydrated, error: null });
    } catch (error) {
      setImported({ loading: false, items: [], error });
    }
  }, []);

  useEffect(() => {
    if (!canView) return;
    loadProvider();
    loadImported();
  }, [canView, loadImported, loadProvider]);

  const importedMetaIds = useMemo(
    () => new Set(imported.items.map(item => item.metaFlowId)),
    [imported.items],
  );

  const importedByMetaId = useMemo(
    () => new Map(imported.items.map(item => [item.metaFlowId, item])),
    [imported.items],
  );

  const reviewProvider = async item => {
    if (providerDetailLoading) return;
    const localDefinition = importedByMetaId.get(item.metaFlowId);
    setProviderDetail({ ...item, pending: true });
    setProviderDetailError(null);
    setActionError(null);
    setDataExchangeSchema("");
    setDataExchangeSchemaError("");
    if (localDefinition && isDataExchangeMode(localDefinition.dataChannelMode)) {
      setProviderDetail({
        ...item,
        isEndpointless: false,
        isImportEligible: false,
        validationErrors: [],
        warnings: Array.isArray(item.warnings) ? item.warnings : [],
      });
      setProviderDetailLoading(false);
      return;
    }
    setProviderDetailLoading(true);
    try {
      setProviderDetail(await getProviderFlow(item.metaFlowId));
    } catch (error) {
      setProviderDetailError(error);
    } finally {
      setProviderDetailLoading(false);
    }
  };

  const viewImported = async item => {
    if (importedDetailLoading) return;
    setImportedDetail(item.detail || { ...item, pending: true });
    setImportedDetailError(null);
    setActionError(null);
    setImportedDetailLoading(true);
    try {
      setImportedDetail(await getImportedFlow(item.id));
    } catch (error) {
      setImportedDetailError(error);
    } finally {
      setImportedDetailLoading(false);
    }
  };

  const refreshAfterMutation = async () => {
    await Promise.all([loadProvider(), loadImported()]);
  };

  const prepareDataExchangeRegistration = () => {
    let schema;
    try {
      schema = JSON.parse(dataExchangeSchema);
    } catch {
      setDataExchangeSchemaError("Enter valid Flow JSON.");
      return;
    }
    if (!schema || typeof schema !== "object" || Array.isArray(schema)) {
      setDataExchangeSchemaError("Flow JSON must have an object root.");
      return;
    }
    setDataExchangeSchemaError("");
    setConfirmation({
      type: "register",
      metaFlowId: providerDetail.metaFlowId,
      payload: {
        name: providerDetail.name,
        metaFlowId: providerDetail.metaFlowId,
        schema,
        flowJsonVersion: providerDetail.flowJsonVersion || null,
        dataApiVersion: providerDetail.dataApiVersion || null,
      },
    });
  };

  const executeConfirmedAction = async () => {
    if (!confirmation || busyAction || actionLockRef.current) return;
    const current = confirmation;
    actionLockRef.current = true;
    setBusyAction(current.type);
    setActionError(null);
    try {
      if (current.type === "import") {
        const result = await importProviderFlow(current.metaFlowId);
        if (result?.isExisting) {
          toast.info("This WhatsApp Form is already imported and up to date.");
        } else {
          toast.success("WhatsApp Form imported successfully.");
        }
        setProviderDetail(null);
        setActiveTab("imported");
      } else if (current.type === "register") {
        await registerWhatsAppFlowDataExchange(current.payload);
        toast.success("Data Exchange Flow registered successfully.");
        setProviderDetail(null);
        setActiveTab("imported");
      } else if (current.type === "resync") {
        const result = await resyncImportedFlow(current.id);
        if (result?.isUnchanged) {
          toast.success("This WhatsApp Form is up to date.");
        }
        if (Array.isArray(result?.warnings) && result.warnings.length > 0) {
          toast.warning(result.warnings[0]?.message || "Meta returned a provider warning.");
        }
        setImportedDetail(await getImportedFlow(current.id));
      } else if (current.type === "archive") {
        await archiveImportedFlow(current.id);
        toast.success("WhatsApp Form archived locally. The Meta Flow was not changed.");
        setImportedDetail(await getImportedFlow(current.id));
      }
      setConfirmation(null);
      await refreshAfterMutation();
    } catch (error) {
      if (current.type === "register" && error?.code === "WHATSAPP_FLOW_ALREADY_REGISTERED") {
        let rows = [];
        try {
          rows = await listImportedFlows();
        } catch {
          // Preserve the original registration conflict when refresh is unavailable.
        }
        if (Array.isArray(rows) && rows.some(item => item.metaFlowId === current.metaFlowId)) {
          setConfirmation(null);
          setProviderDetail(null);
          setActiveTab("imported");
          toast.info("This Data Exchange Flow is already registered.");
          await refreshAfterMutation();
          return;
        }
      }
      setConfirmation(null);
      setActionError(error);
      toast.error(getWhatsAppFlowErrorMessage(error));
    } finally {
      actionLockRef.current = false;
      setBusyAction("");
    }
  };

  if (!canView) {
    return (
      <div className="p-6">
        <ErrorState
          error={{
            code: "WHATSAPP_FLOW_PERMISSION_DENIED",
            message: "WhatsApp Forms view permission is required.",
          }}
          onRetry={() => window.location.reload()}
        />
      </div>
    );
  }

  const currentImportedRevision = importedDetail?.currentPublishedRevision;
  const importedIsDataExchange = isDataExchangeMode(importedDetail?.dataChannelMode);
  const providerLocalDefinition = providerDetail
    ? importedByMetaId.get(providerDetail.metaFlowId)
    : null;
  const providerIsDataExchange = providerDetail?.isEndpointless === false;

  return (
    <main className="min-h-[calc(100vh-80px)] bg-slate-50 p-4 sm:p-6">
      <div className="mx-auto max-w-7xl">
        <header className="mb-5 flex items-start gap-3 px-1 py-2">
          <span className="rounded-xl bg-emerald-600 p-2.5 text-white shadow-sm">
            <ClipboardList size={21} aria-hidden="true" />
          </span>
          <div>
          <h1 className="text-xl font-semibold text-slate-950">
            WhatsApp Forms
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Connect and manage published WhatsApp Flows for use in your automations.
          </p>
          </div>
        </header>

        <div className="mb-5 flex gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
          <Info className="mt-0.5 shrink-0" size={19} />
          <p>
            Forms must already be created and published in Meta. Import published
            Endpointless Flows or register published Data Exchange Flows from Meta.
            Creating, editing and publishing Meta Flows will be added later.
          </p>
        </div>

        <div className="mb-5 flex w-fit rounded-lg border border-slate-200 bg-white p-1 shadow-sm">
          {[
            ["provider", "Available in Meta"],
            ["imported", "Connected Forms"],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setActiveTab(key)}
              className={`rounded-md px-4 py-2 text-sm font-semibold ${
                activeTab === key
                  ? "bg-emerald-700 text-white"
                  : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {actionError && (
          <div role="alert" className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
            {getWhatsAppFlowErrorMessage(actionError)}
          </div>
        )}

        {activeTab === "provider" && (
          <section aria-label="Available in Meta" className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <h2 className="font-semibold text-slate-900">Available in Meta</h2>
                <p className="text-sm text-slate-500">
                  Review provider details before importing or registering a Form.
                </p>
              </div>
              <button
                type="button"
                onClick={loadProvider}
                disabled={provider.loading}
                className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                <RefreshCw size={16} className={provider.loading ? "animate-spin" : ""} />
                Refresh
              </button>
            </div>

            {provider.loading ? (
              <LoadingSkeleton />
            ) : provider.error ? (
              <ErrorState error={provider.error} onRetry={loadProvider} />
            ) : provider.items.length === 0 ? (
              <EmptyState
                title="No published Forms found in Meta"
                message="Publish an Endpointless or Data Exchange Flow in the connected Meta WABA, then refresh this list."
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200 text-left text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-3 py-3">Flow</th>
                      <th className="px-3 py-3">Status</th>
                      <th className="px-3 py-3">Versions</th>
                      <th className="px-3 py-3">Eligibility</th>
                      <th className="px-3 py-3">Connection state</th>
                      <th className="px-3 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {provider.items.map(item => {
                      const isImported = importedMetaIds.has(item.metaFlowId);
                      return (
                        <tr key={item.metaFlowId} className="align-top">
                          <td className="px-3 py-4">
                            <p className="font-semibold text-slate-900">{item.name}</p>
                            <p className="mt-1 font-mono text-xs text-slate-500">{item.metaFlowId}</p>
                            <p className="mt-2 text-xs text-slate-500">
                              {(item.categories || []).join(", ") || "No category"}
                            </p>
                            <WarningList warnings={item.warnings} />
                          </td>
                          <td className="px-3 py-4"><StatusBadge status={item.status} /></td>
                          <td className="px-3 py-4 text-slate-600">
                            <div>JSON {text(item.flowJsonVersion)}</div>
                            <div>Data API {text(item.dataApiVersion)}</div>
                          </td>
                          <td className="px-3 py-4"><Badge tone="slate">Review required</Badge></td>
                          <td className="px-3 py-4">
                            {isImported ? <Badge tone="green">Connected</Badge> : <Badge>Not connected</Badge>}
                          </td>
                          <td className="px-3 py-4 text-right">
                            <button
                              type="button"
                              onClick={() => reviewProvider(item)}
                              disabled={providerDetailLoading || imported.loading}
                              className="inline-flex items-center gap-2 rounded-md bg-emerald-700 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-800 disabled:opacity-50"
                            >
                              <Eye size={16} />
                              Review
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        {activeTab === "imported" && (
          <section aria-label="Connected Forms" className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <h2 className="font-semibold text-slate-900">Connected Forms</h2>
                <p className="text-sm text-slate-500">
                  View immutable published revisions for Endpointless and Data Exchange Forms.
                </p>
              </div>
              <button
                type="button"
                onClick={loadImported}
                disabled={imported.loading}
                className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                <RefreshCw size={16} className={imported.loading ? "animate-spin" : ""} />
                Refresh
              </button>
            </div>

            {imported.loading ? (
              <LoadingSkeleton />
            ) : imported.error ? (
              <ErrorState error={imported.error} onRetry={loadImported} />
            ) : imported.items.length === 0 ? (
              <EmptyState
                title="No connected WhatsApp Forms"
                message="Review an eligible published Flow from the Available in Meta tab."
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200 text-left text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-3 py-3">Form</th>
                      <th className="px-3 py-3">Mode</th>
                      <th className="px-3 py-3">Published revision</th>
                      <th className="px-3 py-3">Structure</th>
                      <th className="px-3 py-3">Updated</th>
                      <th className="px-3 py-3">State</th>
                      <th className="px-3 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {imported.items.map(item => {
                      const detail = item.detail;
                      const revision = detail?.currentPublishedRevision;
                      return (
                        <tr key={item.id}>
                          <td className="px-3 py-4">
                            <p className="font-semibold text-slate-900">{item.name}</p>
                            <p className="mt-1 font-mono text-xs text-slate-500">{item.metaFlowId}</p>
                          </td>
                          <td className="px-3 py-4">{modeLabel(item.dataChannelMode)}</td>
                          <td className="px-3 py-4">{revision?.revisionNumber ?? "—"}</td>
                          <td className="px-3 py-4">
                            {isDataExchangeMode(item.dataChannelMode)
                              ? "Runtime bindings"
                              : `${revision?.fields?.length ?? 0} response fields`}
                          </td>
                          <td className="px-3 py-4 text-slate-600">{formatDate(item.updatedAtUtc)}</td>
                          <td className="px-3 py-4">
                            {item.isArchived ? <Badge tone="amber">Archived</Badge> : <Badge tone="green">Active</Badge>}
                          </td>
                          <td className="px-3 py-4 text-right">
                            <button
                              type="button"
                              onClick={() => viewImported(item)}
                              disabled={importedDetailLoading}
                              className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-3 py-2 font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                            >
                              <Eye size={16} />
                              View
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}
      </div>

      <Modal
        open={Boolean(providerDetail)}
        title="Review provider WhatsApp Form"
        onClose={() => {
          if (!busyAction) {
            setProviderDetail(null);
            setProviderDetailError(null);
            setActionError(null);
          }
        }}
      >
        {providerDetailLoading ? (
          <LoadingSkeleton rows={3} />
        ) : providerDetailError ? (
          <ErrorState
            error={providerDetailError}
            onRetry={() => reviewProvider(providerDetail)}
          />
        ) : (
          <>
            <DetailGrid
              items={[
                { label: "Name", value: providerDetail?.name },
                ...(providerLocalDefinition
                  ? [{ label: "Local display name", value: providerLocalDefinition.name }]
                  : []),
                { label: "Meta Flow ID", value: providerDetail?.metaFlowId },
                { label: "Status", value: <StatusBadge status={providerDetail?.status} /> },
                { label: "Categories", value: providerDetail?.categories?.join(", ") || "—" },
                { label: "JSON version", value: text(providerDetail?.flowJsonVersion) },
                { label: "Data API version", value: text(providerDetail?.dataApiVersion) },
                {
                  label: "Data-channel mode",
                  value: providerDetail?.isEndpointless ? "Endpointless" : "Data Exchange",
                },
                {
                  label: "Validation",
                  value:
                    providerDetail?.validationErrors?.length > 0
                      ? "Validation Error"
                      : "No provider validation errors",
                },
                {
                  label: "Local registration",
                  value: providerLocalDefinition ? "Registered" : "Not registered",
                },
              ]}
            />

            {providerDetail?.validationErrors?.length > 0 && (
              <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4">
                <h3 className="font-semibold text-red-800">Validation errors</h3>
                <ul className="mt-2 space-y-2 text-sm text-red-700">
                  {providerDetail.validationErrors.map((error, index) => (
                    <li key={`${error?.code || "validation"}-${index}`}>
                      {error?.message || "Meta validation error."}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-4 rounded-lg border border-slate-200 p-4">
              <h3 className="font-semibold text-slate-900">Connection eligibility</h3>
              {providerLocalDefinition ? (
                <p className="mt-2 flex items-center gap-2 text-sm text-emerald-700">
                  <CheckCircle2 size={17} />
                  This Flow is registered in XploreByte.
                </p>
              ) : isEndpointlessImportEligible(providerDetail) ? (
                <p className="mt-2 flex items-center gap-2 text-sm text-emerald-700">
                  <CheckCircle2 size={17} />
                  Published Endpointless Flow ready for import.
                </p>
              ) : isDataExchangeRegistrationEligible(providerDetail) ? (
                <p className="mt-2 flex items-center gap-2 text-sm text-emerald-700">
                  <CheckCircle2 size={17} />
                  Published Data Exchange Flow ready for registration.
                </p>
              ) : (
                <p className="mt-2 flex items-center gap-2 text-sm text-red-700">
                  <AlertTriangle size={17} />
                  {providerDetail?.status !== "PUBLISHED"
                    ? "Only published Meta Flows can be connected."
                    : "This Flow has provider validation errors and cannot be connected."}
                </p>
              )}
              <div className="mt-3"><WarningList warnings={providerDetail?.warnings} /></div>
            </div>

            {providerIsDataExchange &&
              !providerLocalDefinition &&
              isDataExchangeRegistrationEligible(providerDetail) && (
                <div className="mt-4 rounded-lg border border-slate-200 p-4">
                  <label htmlFor="data-exchange-schema" className="font-semibold text-slate-900">
                    Published Flow JSON
                  </label>
                  <p className="mt-1 text-sm text-slate-600">
                    Paste the published FLOW_JSON object from Meta. XploreByte stores it as an immutable revision.
                  </p>
                  <textarea
                    id="data-exchange-schema"
                    value={dataExchangeSchema}
                    onChange={event => {
                      setDataExchangeSchema(event.target.value);
                      setDataExchangeSchemaError("");
                    }}
                    rows={10}
                    spellCheck={false}
                    className="mt-3 w-full rounded-md border border-slate-300 p-3 font-mono text-xs text-slate-800 focus:border-emerald-600 focus:outline-none focus:ring-1 focus:ring-emerald-600"
                    placeholder='{"version":"7.3","screens":[]}'
                  />
                  {dataExchangeSchemaError && (
                    <p role="alert" className="mt-2 text-sm text-red-700">{dataExchangeSchemaError}</p>
                  )}
                </div>
              )}

            {!providerLocalDefinition && !canImport && (
              <p className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                {providerIsDataExchange ? "Registration" : "Import"} requires both Create and Publish permissions.
              </p>
            )}

            {actionError && (
              <p role="alert" className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                {getWhatsAppFlowErrorMessage(actionError)}
              </p>
            )}

            <div className="mt-5 flex justify-end">
              {providerLocalDefinition ? (
                <button
                  type="button"
                  onClick={() => {
                    setProviderDetail(null);
                    viewImported(providerLocalDefinition);
                  }}
                  className="rounded-md bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-800"
                >
                  View
                </button>
              ) : providerIsDataExchange ? (
                <button
                  type="button"
                  onClick={prepareDataExchangeRegistration}
                  disabled={!canImport || !isDataExchangeRegistrationEligible(providerDetail) || Boolean(busyAction)}
                  className="rounded-md bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Register
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() =>
                    setConfirmation({
                      type: "import",
                      metaFlowId: providerDetail.metaFlowId,
                    })
                  }
                  disabled={!canImport || !isEndpointlessImportEligible(providerDetail) || Boolean(busyAction)}
                  className="rounded-md bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Import
                </button>
              )}
            </div>
          </>
        )}
      </Modal>

      <Modal
        open={Boolean(importedDetail)}
        title="Connected WhatsApp Form"
        onClose={() => {
          if (!busyAction) {
            setImportedDetail(null);
            setImportedDetailError(null);
            setActionError(null);
          }
        }}
        width="max-w-4xl"
      >
        {importedDetailLoading ? (
          <LoadingSkeleton rows={4} />
        ) : importedDetailError ? (
          <ErrorState
            error={importedDetailError}
            onRetry={() => viewImported(importedDetail)}
          />
        ) : (
          <>
            <DetailGrid
              items={[
                { label: "Name", value: importedDetail?.name },
                { label: "Meta Flow ID", value: importedDetail?.metaFlowId },
                { label: "Local definition ID", value: importedDetail?.id },
                { label: "Data-channel mode", value: modeLabel(importedDetail?.dataChannelMode) },
                { label: "Archive state", value: importedDetail?.isArchived ? "Archived" : "Active" },
                { label: "Revision number", value: currentImportedRevision?.revisionNumber },
                { label: "Provider revision", value: currentImportedRevision?.providerRevisionId },
                { label: "Revision status", value: currentImportedRevision?.status },
                { label: "Flow JSON version", value: currentImportedRevision?.flowJsonVersion },
                { label: "Data API version", value: currentImportedRevision?.dataApiVersion },
                { label: "Published", value: formatDate(currentImportedRevision?.publishedAtUtc) },
              ]}
            />

            {importedIsDataExchange ? (
              <div className="mt-5 rounded-lg border border-blue-200 bg-blue-50 p-4">
                <h3 className="font-semibold text-blue-950">Data Exchange Structure</h3>
                <p className="mt-1 text-sm text-blue-900">
                  This Data Exchange Flow uses runtime screen and data bindings rather
                  than Endpointless response-field mappings.
                </p>
              </div>
            ) : (
              <div className="mt-5">
                <h3 className="font-semibold text-slate-900">Response schema</h3>
                <p className="mt-1 text-sm text-slate-600">
                  These fields come from the published Meta Flow. Field mappings will
                  be configured later inside the Whats Form automation node.
                </p>
                <div className="mt-3 overflow-x-auto rounded-lg border border-slate-200">
                  <table className="min-w-full divide-y divide-slate-200 text-left text-sm">
                    <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-3 py-2.5">Response key</th>
                        <th className="px-3 py-2.5">Type</th>
                        <th className="px-3 py-2.5">Required</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {(currentImportedRevision?.fields || []).map(field => (
                        <tr key={field.responseKey}>
                          <td className="px-3 py-3 font-mono text-xs text-slate-900">{field.responseKey}</td>
                          <td className="px-3 py-3">{text(field.responseType)}</td>
                          <td className="px-3 py-3">{field.isRequired ? "Yes" : "No"}</td>
                        </tr>
                      ))}
                      {(currentImportedRevision?.fields || []).length === 0 && (
                        <tr>
                          <td colSpan={3} className="px-3 py-6 text-center text-slate-500">
                            No normalized response fields are available.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="mt-5"><WarningList warnings={importedDetail?.warnings} /></div>
            {actionError && (
              <p role="alert" className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                {getWhatsAppFlowErrorMessage(actionError)}
              </p>
            )}

            <div className="mt-5 flex flex-wrap justify-end gap-3">
              {!importedIsDataExchange && (
                <button
                  type="button"
                  disabled={!canResync || Boolean(busyAction) || importedDetail?.isArchived}
                  title={!canResync ? "Edit permission required" : undefined}
                  onClick={() =>
                    setConfirmation({ type: "resync", id: importedDetail.id })
                  }
                  className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <RefreshCw size={16} />
                  Resync
                </button>
              )}
              <button
                type="button"
                disabled={!canArchive || Boolean(busyAction) || importedDetail?.isArchived}
                title={!canArchive ? "Archive permission required" : undefined}
                onClick={() =>
                  setConfirmation({ type: "archive", id: importedDetail.id })
                }
                className="inline-flex items-center gap-2 rounded-md bg-amber-700 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Archive size={16} />
                Archive
              </button>
            </div>
          </>
        )}
      </Modal>

      <ActionConfirmation
        confirmation={confirmation}
        busy={Boolean(busyAction)}
        onCancel={() => {
          if (!busyAction) setConfirmation(null);
        }}
        onConfirm={executeConfirmedAction}
      />
    </main>
  );
}
